import { useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useHelpPreferences } from './useHelpPreferences';
import {
  proactiveTipDefinitions,
  type ProactiveTipDefinition,
} from '../config/proactiveTips';

// ============================================
// MAPEAMENTO DE PATHNAME → SEÇÃO
// ============================================

/**
 * Determina a seção atual baseada no pathname da rota.
 * Retorna undefined se nenhuma seção corresponder.
 */
export function getSectionFromPathname(pathname: string): string | undefined {
  // Ordem importa: prefixos mais específicos primeiro
  if (/^\/admin\/tokenization(\/|$)/.test(pathname)) return 'tokenization';
  if (/^\/admin\/compliance(\/|$)/.test(pathname)) return 'compliance';
  if (/^\/admin\/credit(\/|$)/.test(pathname)) return 'credit';
  if (/^\/banking\/credit(\/|$)/.test(pathname)) return 'credit';
  if (/^\/admin\/cards(\/|$)/.test(pathname)) return 'cards';
  if (/^\/banking\/cards(\/|$)/.test(pathname)) return 'cards';
  if (/^\/admin\/investments(\/|$)/.test(pathname)) return 'investments';
  if (/^\/banking\/investments(\/|$)/.test(pathname)) return 'investments';
  if (/^\/banking\/crypto(\/|$)/.test(pathname)) return 'crypto';
  if (/^\/banking\/defi(\/|$)/.test(pathname)) return 'crypto';
  if (/^\/banking\/staking(\/|$)/.test(pathname)) return 'crypto';
  if (/^\/banking\/yield-farming(\/|$)/.test(pathname)) return 'crypto';
  if (/^\/admin\/contracts(\/|$)/.test(pathname)) return 'contracts';
  if (/^\/banking\/contracts(\/|$)/.test(pathname)) return 'contracts';
  if (/^\/admin\/kyc(\/|$)/.test(pathname)) return 'kyc';
  if (/^\/banking\/kyc(\/|$)/.test(pathname)) return 'kyc';
  if (/^\/banking\/settings(\/|$)/.test(pathname)) return 'settings';
  if (/^\/banking(\/|$)/.test(pathname)) return 'banking';

  return undefined;
}

// ============================================
// HOOK
// ============================================

export function useProactiveTips() {
  const { pathname } = useLocation();
  const { hasDismissedTip, markTipDismissed } = useHelpPreferences();

  const currentSection = useMemo(
    () => getSectionFromPathname(pathname),
    [pathname],
  );

  /** Retorna a dica da seção atual, ou null se já dispensada ou sem seção */
  const getCurrentTip = useCallback((): ProactiveTipDefinition | null => {
    if (!currentSection) return null;

    const tip = proactiveTipDefinitions.find(
      (t) => t.section === currentSection,
    );
    if (!tip) return null;

    if (hasDismissedTip(tip.id)) return null;

    return tip;
  }, [currentSection, hasDismissedTip]);

  /** Dispensa a dica da seção atual e persiste a preferência */
  const dismissCurrentTip = useCallback((): void => {
    const tip = getCurrentTip();
    if (tip) {
      markTipDismissed(tip.id);
    }
  }, [getCurrentTip, markTipDismissed]);

  return {
    currentSection,
    getCurrentTip,
    dismissCurrentTip,
  };
}
